import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {VirtualFileService} from '@twpub/core/services/virtual-file.service';
import {NGXLogger} from 'ngx-logger';

import {ImageData} from '@twpub/core/models/image-data';
import {forkJoin} from 'rxjs';
import {NoImageComponent} from './no-image/no-image.component';
import {hasPropChanges} from '@twpub/core/utils';

@Component({
  selector: 'pub-card-image',
  templateUrl: './card-image.component.html',
  styleUrls: ['./card-image.component.scss'],
  standalone: true,
  imports: [NoImageComponent]
})
export class CardImageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() entryId?: number = 0;
  @Input() fileIds: number[] = [];

  images?: ImageData[];
  imageData?: ImageData;
  galleryId: string = '';
  imageReady: boolean = false;
  private thumbnailObjectUrl?: string;

  constructor(private logger: NGXLogger, private fileService: VirtualFileService) {
  }

  ngOnInit(): void {
    this.loadImages();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (hasPropChanges(changes, ['entryId', 'fileIds'])) {
      this.loadImages();
    }
  }

  ngOnDestroy(): void {
    this.revokeThumbnailObjectUrl();
  }

  private loadImages() {
    this.imageReady = false;
    this.revokeThumbnailObjectUrl();

    if (!this.fileIds?.length) {
      this.images = undefined;
      this.imageData = undefined;
      return;
    }

    this.logger.debug('CardImageComponent.loadImages:', {fileIds: this.fileIds})
    this.galleryId = 'gallery-' + this.entryId;
    const imageObs = this.fileIds.map(fileId => this.fileService.loadImageData(fileId));
    forkJoin(imageObs).subscribe({
      next: (imageDatas) => {
        this.images = imageDatas;
        this.imageData = imageDatas[0];
        this.loadThumbnail(this.fileIds[0]);
      },
      error: (error) => {
        this.logger.error('Failed to load images', error);
      }
    });
  }

  private loadThumbnail(fileId: number) {
    this.fileService.getContentBlob(fileId, true).subscribe({
      next: (blob) => {
        const thumbUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          if (this.imageData) {
            const scale = 1.3
            this.revokeThumbnailObjectUrl();
            this.thumbnailObjectUrl = thumbUrl;
            this.imageData.thumbUrl = thumbUrl;
            this.imageData.thumbWidth = img.width * scale;
            this.imageData.thumbHeight = img.height * scale;
            this.imageReady = true;
          } else {
            URL.revokeObjectURL(thumbUrl);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(thumbUrl);
          this.logger.warn('Failed to load thumbnail image', {fileId});
        };
        img.src = thumbUrl;
      },
      error: (error) => {
        this.logger.error('Failed to load thumbnail', error);
      }
    });
  }

  private revokeThumbnailObjectUrl() {
    if (this.thumbnailObjectUrl) {
      URL.revokeObjectURL(this.thumbnailObjectUrl);
      this.thumbnailObjectUrl = undefined;
    }
  }
}
